import React, { useEffect } from 'react';
import { InputLabel } from 'src/shared/components/ui/input-label';
import useProject from 'src/shared/hooks/useProject';
import useIsViewed from 'src/shared/hooks/useIsViewed';
import Loader from 'src/shared/components/loader/Loader';
import useRefWithRerender from 'src/shared/hooks/useRefWithRerender';
import useTestTypeFeatures from 'src/shared/hooks/useTestTypeFeatures';

import { useAppDispatch, useAppSelector } from 'src/store';
import { useSpeciesMetricsQuery } from 'src/app/dashboard/state/metrics/metricsGraphSlice';
import { getCurrentDashboardUIState } from 'src/app/dashboard/state/dashboardGeneralSelectors';
import { updateIucnMetricUiState } from 'src/app/dashboard/state/dashboardGeneralSlice';

import IucnTable from 'src/app/dashboard/components/metrics/metrics-table/components/IucnTable';
import {
    filterByRedListStatus,
    filterByGRIISList,
    filterByCHEGDStatus,
} from 'src/app/dashboard/components/metrics/metrics-table/metricsTableHelpers';
import useDashboardAnalytics from 'src/app/dashboard/hooks/useDashboardAnalytics';

import SamplingEventFilter from 'src/shared/components/filters/sampling-event-filter/SamplingEventFilter';
import useProjectFilters, { SamplingEventFilterData } from 'src/shared/hooks/useProjectFilters';
import styles from './MetricsTable.module.scss';
import ChegdTable from './components/ChegdTable';

const MetricsTable = () => {
    const { isSingleTimepoint } = useProject();

    const { currentProjectId, currentProjectSelectedHabitatAssayType: habitatAssayType, samplingEventFilterData } = useProjectFilters();

    const uiState = useAppSelector(getCurrentDashboardUIState);
    const dispatch = useAppDispatch();
    const [ref, callbackRef] = useRefWithRerender<HTMLDivElement>(null);
    const isViewed = useIsViewed(ref);
    const { trackIUCNTableViewed } = useDashboardAnalytics();
    const { hasIucnRedListFeature, hasInvasivesFeature, hasChegdFeature } = useTestTypeFeatures();

    const hideDateFilter = isSingleTimepoint;

    const {
        iucnMetric: { samplingEvent },
    } = uiState;

    const { currentData, isFetching } = useSpeciesMetricsQuery(
        {
            projectId: currentProjectId || '',
            samples: {
                fromDate: isSingleTimepoint ? undefined : samplingEvent?.fromDate,
                toDate: isSingleTimepoint ? undefined : samplingEvent?.toDate,
                habitatAssay: habitatAssayType?.key || '',
            },
        },
        {
            skip: !currentProjectId || !habitatAssayType || !samplingEvent,
        }
    );

    useEffect(() => {
        if (isViewed) {
            trackIUCNTableViewed();
        }
    }, [isViewed]);

    useEffect(() => {
        if (!samplingEventFilterData || samplingEventFilterData.length === 0) {
            return;
        }

        if (!samplingEvent) {
            onTimePointChange(samplingEventFilterData[0]);
        }
    }, [samplingEventFilterData]);

    const onTimePointChange = (samplingEventValue: SamplingEventFilterData) => {
        dispatch(updateIucnMetricUiState({ samplingEvent: samplingEventValue }));
    };

    if (!currentData || isFetching) {
        return <Loader />;
    }

    const { speciesMetrics: speciesMetricsData } = currentData;
    const { taxonomySummary } = speciesMetricsData;

    const iucnRedLists = filterByRedListStatus(taxonomySummary);
    const invasiveLists = filterByGRIISList(taxonomySummary);
    const chegdSpeciesList = filterByCHEGDStatus(taxonomySummary);

    const header = (
        <div>
            <div className={styles.filter} ref={callbackRef}>
                <div className={styles.title}>Results in this table are only displayed for one quarter</div>
                <div>|</div>
                <div className='flex flex-row items-center gap-2'>
                    <InputLabel className={styles.filterLabel}>Display detections in</InputLabel>
                    <SamplingEventFilter
                        selectedSamplingEvent={samplingEvent}
                        samplingEventFilterData={samplingEventFilterData}
                        onChange={value => onTimePointChange(value)}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.iucnMetrics}>
            {!hideDateFilter && header}
            <div>
                {hasIucnRedListFeature && samplingEvent && (
                    <IucnTable
                        taxonomySummary={[...iucnRedLists]}
                        title='IUCN Red List Species'
                        type='redLists'
                        helpArticleSlug='iucn-status'
                        samplingEvent={samplingEvent}
                    />
                )}
                {hasChegdFeature && <ChegdTable data={chegdSpeciesList} />}
                {!hasChegdFeature && hasInvasivesFeature && samplingEvent && (
                    <IucnTable
                        taxonomySummary={[...invasiveLists]}
                        title='Invasive Species'
                        type='invasiveLists'
                        helpArticleSlug='invasive-species'
                        samplingEvent={samplingEvent}
                    />
                )}
            </div>
        </div>
    );
};

export default MetricsTable;
